import { Box, Grid, Typography } from '@mui/material';
import Carousel from 'components/Carousel/Carousel';
import { ListCarousel } from 'components/ListCarousel/ListCarousel';
import Product from 'components/Product/Product';
import { AddToCartButton } from 'components/Product/components/AddToCartButton';
import ProductPrice from 'components/Product/components/ProductPrice';
import { Variants } from 'components/Product/components/Variants';
import PublicQuantity from 'components/PublicQuantity';
import { ShowHide } from 'components/common/ShowHide';
import { Product as ProductType } from 'generated/graphql';
import { useCart } from 'hooks/useCart';
import { useProduct } from 'hooks/useProduct';
import { useEffect, useState } from 'react';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { BundledProducts } from 'components/common/BundledProducts';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { ProductHelper } from 'helpers/ProductHelper';
import { ConsultantCta } from './components/ConsultantCta';
import { ArticleNumber } from './components/ArticleNumber';

export type ProductDetailsProps = {
    product: ProductType;
    recommended: ProductType[];
    recommendedRelated: ProductType[];
};

export default function ProductDetails(props: ProductDetailsProps) {
    const { product, recommended, recommendedRelated } = props;
    const { isGuestCheckoutEnabled } = useFeatureFlags();
    const { t } = useServerTranslation();
    const { addToCart } = useCart();
    const {
        isProductWrapper,
        isSimpleProductSelected,
        isWrapperProductSelected,
        isAddedToCart,
        selectedProductVariant,
        quantityInCart,
        setSelectedProductId,
    } = useProduct(product, { changeUrlOnVariantSelect: true });
    const [selectedQuantity, setSelectedQuantity] = useState(quantityInCart ?? 1);

    useEffect(() => {
        setSelectedQuantity(quantityInCart ?? 1);
    }, [quantityInCart]);

    const onAddToCartHandler = () => {
        const childId = isProductWrapper ? selectedProductVariant?.id ?? undefined : undefined;

        addToCart(
            {
                productId: product.id,
                childId,
                quantity: selectedQuantity,
            },
        );
    };

    const recommendedProducts = useRecommendedItems({ recommended });

    // Related to the commented out code below in the jsx.
    // TODO: recommendedRelated are currently randomized.
    // TODO: reimplement this when prioritized.
    // const recommendedRelatedProducts = useRecommendedItems({ recommended: recommendedRelated });

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={6}>
                <Carousel images={selectedProductVariant.images} />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
                <Typography
                    variant="h1"
                    sx={{
                        fontWeight: '400',
                        fontSize: '25px',
                    }}
                    paddingBottom={2}
                >
                    {selectedProductVariant.name}
                </Typography>
                <div>
                    {isProductWrapper ? (
                        <Box marginY={2}>
                            <Variants
                                product={product}
                                selectedProductId={selectedProductVariant.id}
                                onSelect={(productId) => setSelectedProductId(productId)}
                            />
                        </Box>
                    ) : null}
                </div>
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns={{ xs: '1fr 1fr', sm: '2fr 1fr 1fr' }}
                >
                    <Box>
                        <ShowHide show={isSimpleProductSelected}>
                            <Box sx={{ marginTop: 2 }}>
                                <ProductPrice product={selectedProductVariant} size={2} />
                            </Box>
                        </ShowHide>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                        }}
                    >
                        {isSimpleProductSelected ? (
                            <PublicQuantity
                                value={selectedQuantity}
                                onChange={(value) => {
                                    setSelectedQuantity(value);
                                }}
                            />
                        ) : null}
                    </Box>
                    <Box sx={{
                        display: 'grid',
                        alignItems: 'flex-end',
                        gridColumn: { xs: '1 / 3', sm: '3 / 4' },
                    }}
                    >
                        {isWrapperProductSelected ? (
                            <Typography marginBottom={1} textAlign="center" variant="subtitle2">
                                {t('product.choose-color-to-purchase')}
                            </Typography>
                        ) : null}
                        <AddToCartButton
                            key="addToCartButton"
                            onClick={onAddToCartHandler}
                            disabled={isWrapperProductSelected}
                            isAddedToCart={isAddedToCart}
                        />
                    </Box>
                </Box>
                <Box sx={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                    <Typography
                        sx={{ margin: '0 0 20px 0' }}
                        component="div"
                        dangerouslySetInnerHTML={{ __html: ProductHelper.getProductDescription(selectedProductVariant, product) }}
                    />
                    {isGuestCheckoutEnabled ? null : <ConsultantCta />}

                    {product.volume && (
                        <div>
                            <Typography variant="h3" sx={{ margin: '20px 0 10px 0 ' }}>
                                {t('product.volume')}
                            </Typography>
                            <Typography>{product.volume}</Typography>
                        </div>
                    )}

                    {product.howItWorks && (
                        <div>
                            <Typography variant="h3" sx={{ margin: '20px 0 10px 0 ' }}>
                                {t('product.benefits-of-the-product')}
                            </Typography>
                            <Typography component="div" dangerouslySetInnerHTML={{ __html: product.howItWorks }} />
                        </div>
                    )}

                    {product.applicationTips && (
                        <div>
                            <Typography variant="h3" sx={{ margin: '20px 0 10px 0' }}>
                                {t('product.application-tips')}
                            </Typography>
                            <Typography
                                sx={{ margin: '10px 0 ' }}
                                component="div"
                                dangerouslySetInnerHTML={{ __html: product.applicationTips }}
                            />
                        </div>
                    )}
                    <BundledProducts product={product} stackProps={{ sx: { marginTop: 4 } }} />
                </Box>
                {isSimpleProductSelected && (
                    <ArticleNumber>{selectedProductVariant.sku}</ArticleNumber>
                )}
            </Grid>
            {recommended?.length ? (
                <Grid item xs={12}>
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{
                            color: 'inherit',
                            fontSize: '1.313rem',
                            marginY: 3,
                            paddingX: { sm: '36px' },
                        }}
                    >
                        {t('product.recommended-personal')}
                    </Typography>
                    <ListCarousel
                        items={recommendedProducts}
                        listWrapperStyle={{ marginBottom: 3, flex: 1 }}
                    />
                </Grid>
            ) : null}
            {/* // TODO: recommendedRelated are currently randomized. */}
            {/* // TODO: reimplement this when prioritized. */}
            {/* {recommendedRelated?.length ? ( */}
            {/*    <Grid item xs={12}> */}
            {/*        <Typography variant="h2" component="h2" color="secondary.main" marginY={2}> */}
            {/*            {t('product.recommended-related')} */}
            {/*        </Typography> */}
            {/*        <ListCarousel */}
            {/*            items={recommendedRelatedProducts} */}
            {/*            listWrapperStyle={{ marginBottom: 3, flex: 1 }} */}
            {/*        /> */}
            {/*    </Grid> */}
            {/* ) : null} */}
        </Grid>
    );
}

function useRecommendedItems({ recommended }: { recommended: ProductType[] }) {
    return recommended.map((recommendedProduct) => (
        <Product
            product={recommendedProduct}
            boxProps={{
                width: '220px',
                marginX: 1.5,
            }}
        />
    ));
}
